import React from 'react';

import { FmStack, FmText } from './FmComposition';
import { FmGalleryIdentity } from './FmGalleryIdentity';
import { FmGallerySpecimenPicker, type FmGalleryStatePage } from './FmGallerySpecimenPicker';
import { FmContentWidth } from './FmLayout';
import {
  FmReportPageTyped,
  type FmReportPageTypedProps,
} from './FmReportPageTyped';
import { validateReportChapters } from './FmReportChapter.validation';
import { withPreviewMapStates } from './FmReportPageTyped.preview';

export interface FmReportPageTypedSpecimen {
  id: string;
  label: string;
  note: string;
  descriptor: FmReportPageTypedProps;
}

export interface FmReportPageTypedGalleryProps {
  title: string;
  intro: string;
  specimens: readonly FmReportPageTypedSpecimen[];
  /** Specimen id per page state; the rest are report behaviour variants. */
  states: FmGalleryStatePage;
}

function readSpecimenId(specimens: readonly FmReportPageTypedSpecimen[]): string {
  const requested = new URLSearchParams(window.location.search).get('report-page');
  return specimens.find(({ id }) => id === requested)?.id ?? specimens[0]?.id ?? '';
}

function writeSpecimenId(id: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('report-page', id);
  window.history.pushState({}, '', url);
}

/** Visual-validation selector for complete descriptors emitted by report builders. */
export const FmReportPageTypedGallery: React.FC<FmReportPageTypedGalleryProps> = ({
  title,
  intro,
  specimens,
  states,
}) => {
  const [activeId, setActiveId] = React.useState(() => readSpecimenId(specimens));
  const active = specimens.find(({ id }) => id === activeId) ?? specimens[0];

  React.useEffect(() => {
    const onPopState = (): void => setActiveId(readSpecimenId(specimens));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [specimens]);

  if (!active) return null;
  const descriptor = withPreviewMapStates(active.descriptor);
  validateReportChapters(descriptor.chapters);

  return (
    <section data-testid="report-page-gallery">
      <FmContentWidth width="content">
        <FmStack gap="md">
          <FmText as="h2">{title}</FmText>
          <FmText tone="muted">{intro}</FmText>
          <FmGallerySpecimenPicker
            archetype="Report page"
            specimens={specimens}
            states={states}
            activeId={active.id}
            onSelect={(specimen) => {
              setActiveId(specimen.id);
              writeSpecimenId(specimen.id);
            }}
            testIdPrefix="report-page-specimen-"
          />
          <FmText size="caption" tone="muted">{active.note}</FmText>
          <FmGalleryIdentity
            vocabulary="layout"
            names={[
              ...new Set(
                descriptor.chapters.flatMap((chapter) =>
                  chapter.sections.map(({ section }) => section.arrangement),
                ),
              ),
            ]}
            label={active.label}
            useFor="The report archetype places these layouts; the page shell is not a layout."
            data-testid="report-page-specimen-arrangements"
          />
        </FmStack>
      </FmContentWidth>
      <FmReportPageTyped key={active.id} {...descriptor} />
    </section>
  );
};

FmReportPageTypedGallery.displayName = 'FmReportPageTypedGallery';
